import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const ContactCard = ({ homePageData, getHomePageData }) => {
  const { user: authUser } = useSelector((x) => x.auth);
  const url = process.env.REACT_APP_URL;

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState('');
  const [visible, setVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (homePageData) {
      setTo(homePageData.contact?.to || []);
      setSubject(homePageData.contact?.subject || '');
      setVisible(homePageData.contact?.visible || false);
    }
    // eslint-disable-next-line
  }, [homePageData]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyDown = (e) => {
    if (['Enter', ' ', ',', ';'].includes(e.key)) {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue && isValidEmail(trimmedValue)) {
        if (!to.includes(trimmedValue)) {
          setTo([...to, trimmedValue]);
          setInputValue('');
        } else {
          toast.error(`Duplicate email address: ${trimmedValue}`, { position: 'bottom-left' });
        }
      } else if (trimmedValue) {
        toast.error(`Invalid email address: ${trimmedValue}`, { position: 'bottom-left' });
      }
    } else if (e.key === 'Backspace' && inputValue === '' && to.length > 0) {
      // Remove the last email when Backspace is pressed and input is empty
      setTo(to.slice(0, -1));
    }
  };

  const handleRemoveEmail = (index) => {
    const updatedTo = [...to];
    updatedTo.splice(index, 1);
    setTo(updatedTo);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      // console.log('to:', to)
      const data = {
        'contact.to': to, // Pass the array (even if empty)
        'contact.subject': subject,
        'contact.visible': visible,
      };

      try {
        const response = await fetch(`${url}/front`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
          },
          body: JSON.stringify(data),
        });
        if (response.ok) {
          toast.success('Contact updated successfully!', { position: 'bottom-left' });
          getHomePageData();
        } else {
          const errorResponse = await response.json();
          toast.error(`Failed to update Contact section! ${errorResponse.error || response.statusText}`, { position: 'bottom-left' });
        }
      } catch (error) {
        toast.error(`Error updating Contact section! ${error.message}`, { position: 'bottom-left' });
      }
    },
    [authUser.token, url, getHomePageData, to, subject, visible]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();

        // Check if the active element is inside this card
        const cardElement = document.getElementById('contact-card'); // Add a unique ID to the card
        if (cardElement && cardElement.contains(document.activeElement)) {
          handleSubmit(e); // Trigger handleSubmit only for this card
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
  }

  return (
    <Card id="contact-card" className="mb-3 mt-3">
      <Form data-bs-theme={'dark'}>
        <Card.Header onClick={toggleCollapse}>
          <Row className="header mb-2">
            <Col xs={11} lg={11} className="title">
              <h5>Contact</h5>
            </Col>
            <Col xs={1} lg={1}>
              <i
                className={`bi ${isCollapsed ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`}
                style={{ cursor: 'pointer', paddingTop: '5px' }}
              ></i>
            </Col>
          </Row>
        </Card.Header>
        {!isCollapsed && (
          <>
            <Card.Body>
              <Row className="mb-3">
                <Col>
                  <Form.Group className='mb-3'>
                    <Form.Label>Email subject</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder='Email subject used in the contact emails'
                      value={subject}
                      name='EmailSubject'
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Emails to receive contact messages</Form.Label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        border: '1px solid #ced4da',
                        borderRadius: '0.375rem',
                        padding: '0.375rem 0.75rem',
                      }}
                    >
                      {to.map((email, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.375rem',
                            marginRight: '0.5rem',
                            marginBottom: '0.5rem',
                          }}
                        >
                          {email}
                          <i
                            className="bi bi-x-circle ms-2"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveEmail(index)}
                          ></i>
                        </div>
                      ))}
                      <input
                        type="text"
                        placeholder="Type an email and press Enter, Space, Comma, or Semicolon"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{
                          border: 'none',
                          outline: 'none',
                          flex: '1',
                          minWidth: '150px',
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <Row className="mb-3">
                <Col xs={8} lg={10}>
                  <Form.Check
                    type="checkbox"
                    id="contactVisible"
                    label="Visible"
                    checked={visible}
                    onChange={(e) => setVisible(e.target.checked)}
                  />
                </Col>
                <Col xs={4} lg={2}>
                  <Button variant="primary" className="float-end" onClick={handleSubmit}>
                    <i className="bi bi-floppy"></i> Save
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </>
        )}
      </Form>
    </Card>
  );
};

export default ContactCard;